import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Order, OrderStatus } from "@shared/types";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { AdminLayout } from "./AdminLayout";

const STATUSES: (OrderStatus | "all")[] = ["all", "pending", "confirmed", "shipped", "delivered", "cancelled"];

export function AdminOrders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [status, setStatus] = useState<OrderStatus | "all">("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.admin.orders
      .list(status === "all" ? undefined : status)
      .then(setOrders)
      .finally(() => setLoading(false));
  }, [status]);

  return (
    <AdminLayout>
      <h1 className="mb-6 font-display text-xl font-medium text-ink">Orders</h1>

      <div className="mb-4 flex gap-2 overflow-x-auto">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`shrink-0 rounded-pill px-3 py-1.5 text-xs font-semibold capitalize ${
              status === s ? "bg-ink text-white" : "border border-stone-300 text-ink"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : orders.length === 0 ? (
        <p className="text-sm text-stone-500">No orders here.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-stone-300 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-stone-300 text-xs uppercase tracking-wide text-stone-500">
              <tr>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Placed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {orders.map((o) => (
                <tr key={o.id}>
                  <td className="px-4 py-3">
                    <Link to={`/admin/orders/${o.id}`} className="font-semibold text-ink">
                      {o.orderNumber}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-stone-700">{o.customerName}</td>
                  <td className="px-4 py-3 text-stone-700">{formatMoney(o.subtotalCents, o.currency)}</td>
                  <td className="px-4 py-3">
                    <span className="rounded bg-stone-100 px-2 py-1 text-xs font-semibold capitalize text-stone-700">
                      {o.paymentStatus.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="rounded bg-stone-100 px-2 py-1 text-xs font-semibold capitalize text-stone-700">
                      {o.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-stone-500">{new Date(o.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
